import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FileText, AlertCircle, CheckCircle, Phone, Mail, MapPin } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import '@/styles/animations.css'

export function TermsOfUsePage() {

  return (
    <div className="min-h-screen">
      <Header currentPage="terms" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              <FileText className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Terms of <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Use</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
              Please read these terms carefully before using our website and services. By accessing our site, you agree to these terms.
            </p>
            <p className="text-sm text-gray-400 mt-4 animate-fade-in-up" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>Last updated: January 15, 2024</p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mr-2" />
                <h3 className="text-lg font-semibold text-amber-900">Important Notice</h3>
              </div>
              <p className="text-amber-800">
                These terms govern your use of our website and services. Please read them carefully. 
                If you do not agree with these terms, please do not use our website or services.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-8">
              By accessing and using the Sri Sai Sabari Dental Clinic website and services, you accept and agree to be bound by these Terms of Use. 
              These terms apply to all visitors, users, and others who access or use our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p className="text-gray-600 mb-4">
              Sri Sai Sabari Dental Clinic provides:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Comprehensive dental care services</li>
              <li>Online appointment booking</li>
              <li>Educational content about dental health</li>
              <li>Patient resources and information</li>
              <li>Communication tools for patient care</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
            <p className="text-gray-600 mb-4">
              As a user of our services, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Provide accurate and complete information</li>
              <li>Keep your account information up to date</li>
              <li>Use our services only for lawful purposes</li>
              <li>Respect the intellectual property rights of others</li>
              <li>Not interfere with the proper functioning of our website</li>
              <li>Notify us of any security breaches or unauthorized access</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Appointments and Scheduling</h2>
            <p className="text-gray-600 mb-4">
              For appointment scheduling:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Online bookings are subject to confirmation</li>
              <li>We reserve the right to reschedule appointments if necessary</li>
              <li>Cancellations should be made at least 24 hours in advance</li>
              <li>No-shows may result in charges or scheduling restrictions</li>
              <li>Emergency appointments are handled on a case-by-case basis</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Medical Information Disclaimer</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Medical Disclaimer</h3>
              </div>
              <p className="text-red-800">
                The information provided on this website is for educational purposes only and should not be considered as medical advice. 
                Always consult with Dr. Aishwarya or qualified dental professionals for specific medical concerns.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              All content on this website is protected by intellectual property laws:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Text, images, videos, and other content are owned by Sri Sai Sabari Dental Clinic</li>
              <li>You may not reproduce, distribute, or modify content without permission</li>
              <li>Trademarks and logos are the property of their respective owners</li>
              <li>User-generated content may be used by us for promotional purposes</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
            <p className="text-gray-600 mb-8">
              Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, 
              which is incorporated into these terms by reference. Please review our Privacy Policy to understand our practices.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              Sri Sai Sabari Dental Clinic shall not be liable for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Any indirect, incidental, or consequential damages</li>
              <li>Loss of data or interruption of service</li>
              <li>Damages resulting from use of information on this website</li>
              <li>Third-party content or services linked from our website</li>
              <li>Technical failures or security breaches beyond our reasonable control</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Prohibited Uses</h2>
            <p className="text-gray-600 mb-4">
              You may not use our website or services to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit harmful, offensive, or inappropriate content</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt our services</li>
              <li>Collect user information without consent</li>
              <li>Use automated systems to access our website</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-600 mb-8">
              We reserve the right to terminate or suspend your access to our services at any time, with or without cause, 
              and with or without notice. Upon termination, your right to use our services ceases immediately.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-600 mb-8">
              We may update these terms from time to time. Changes will be posted on this page with an updated effective date. 
              Your continued use of our services after changes constitutes acceptance of the new terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600 mb-8">
              These terms are governed by the laws of India and the state of Tamil Nadu. Any disputes arising from these terms 
              or your use of our services shall be resolved in the courts of Chennai, Tamil Nadu.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Professional Standards</h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-green-900">Our Commitment</h3>
              </div>
              <p className="text-green-800">
                Dr. Aishwarya V and Sri Sai Sabari Dental Clinic are committed to providing professional dental care 
                in accordance with established dental standards and ethical guidelines.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Emergency Situations</h2>
            <p className="text-gray-600 mb-4">
              In case of dental emergencies:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8">
              <li>Call our emergency number: +91-9841817510</li>
              <li>For life-threatening emergencies, call local emergency services</li>
              <li>Our website is not intended for emergency communications</li>
              <li>Emergency appointments are subject to availability</li>
            </ul>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <p className="text-gray-600 mb-4">
                If you have questions about these terms, please contact us:
              </p>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>+91-9841817510</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>smile@sssdental.com</span>
                </div>
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 mr-2 mt-1" />
                  <span>East Main Road, opposite to leobags, Puzhuthivakkam, Madipakkam, Chennai, Tamil Nadu 600091</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/privacy-policy">View Privacy Policy</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/book-appointment">Book Appointment</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}